import { useTranslation } from 'react-i18next';
import { Prompt } from 'Components';
import { Formik, FormikHelpers } from 'formik';
import { getAppRoutes } from 'Pages/App/App.constants';
import { schema } from '../schema';
import { useMutation } from '@apollo/client';
import { CREATE_USER } from 'Services/Api/Users/Mutations';
import { useHistory } from 'react-router';
import CreateUserFormFields from './CreateUserFormFields';
import { checkApolloError } from 'Helpers/graphql';
import { navigateBack } from 'Helpers/navigation';
import { APIErrorCodes } from 'app-types';
import { AddUserValues } from '../User.types';
import { getAddUserSubmitValues } from '../EditUser/EditUser.helpers';
import { AddUserRequest, TwoFactorAuthorizationStatus } from 'Services/Api/Users/Types';
import { useIsModuleSettingDisabled } from '../EditUser/tabs/ModuleSettings/ModuleSettingsContainer';
import {
	isModuleSettingsValidationError,
	validateLMSModuleSettings
} from '../EditUser/tabs/ModuleSettings/ModuleSettings.helpers';
import { useUser } from '../User.hooks';
import EditUserTabs from '../EditUser/EditUserTabs';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

const CreateUser = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { injectMeta } = useUser();
	const [createUser] = useMutation<void, AddUserRequest>(CREATE_USER, { ignoreResults: false });
	const { getIsLMSModuleSettingsDisabled } = useIsModuleSettingDisabled();
	const { setToastInfo } = useDashboardContext();
	const initialValues = {} as AddUserValues;

	const onSubmit = async (
		values: AddUserValues,
		helpers: FormikHelpers<AddUserValues>
	) => {
		try {
			const isModuleSettingsDisabled = getIsLMSModuleSettingsDisabled(values);
			validateLMSModuleSettings(isModuleSettingsDisabled, values);
			const payload = getAddUserSubmitValues(values);
			await createUser({ variables: { input: payload }});
			helpers.resetForm({ values });
			navigateBack(history, getAppRoutes().USERS)
			setToastInfo?.({ message: t('user.createUser.notification'), type: 'success', icon: "check" });
		} catch (e) {
			const error = e as Error;
			if (isModuleSettingsValidationError(error)) {
				helpers.setErrors({ moduleSettings: error.message });
				return;
			}
			helpers.setSubmitting(false);

			const errors = checkApolloError(error).getFieldErrors(
				t,
				injectMeta(schema.describe().fields),
				'user.errorCodes'
			);

			helpers.setErrors(errors ?? {});

			if (errors) return;

			const apolloError = checkApolloError(error);
			let errorMsg = 'errorCodes.genericErrorMessage';
			if (apolloError.is(APIErrorCodes.Forbidden)) errorMsg = `errorCodes.${APIErrorCodes.Forbidden}`;
			else if (apolloError.is(APIErrorCodes.ValidationIsCognitoPhoneNumber)) errorMsg = `user.errorCodes.${APIErrorCodes.ValidationIsCognitoPhoneNumber}`;
			setToastInfo?.({ message: t(errorMsg), type: 'warning' });
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={onSubmit}
			validationSchema={schema}
		>
			{({ dirty, isSubmitting }) => (
				<>
					<Prompt blockTransition={dirty && !isSubmitting}>
					{(helpers) => (
						<EditUserTabs
							{...helpers}
							isProfile={false}
							isLmsHasError={false}
							twoFactorAuthenticationStatus={
								TwoFactorAuthorizationStatus.Setup
							}
						/>
					)}
					</Prompt>
				</>
			)}
		</Formik>
	);
};

export default CreateUser;
